/**
 * BotWhatsApp — componente compartilhado entre AdminPage e DashboardPage.
 *
 * Props:
 *  - users         : lista de usuários já carregada pela página pai
 *  - coordId       : null  → modo ADMIN (filtro por coord. geral disponível)
 *                    uuid  → modo COORD_GERAL (filtra apenas a própria rede)
 *  - coordsGerais  : lista de coords gerais (só usado no modo ADMIN)
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { getSubtree, gerarMsgVerificacao, roleLabel } from '../utils/helpers';

export default function BotWhatsApp({ users, coordId = null, coordsGerais = [] }) {
  const [botTab,   setBotTab]   = useState('painel');
  const [status,   setStatus]   = useState(null);
  const [logs,     setLogs]     = useState([]);
  const [sending,  setSending]  = useState(false);

  // Filtros
  const [filtCoord,       setFiltCoord]       = useState(coordId ? String(coordId) : '');
  const [filtPerfil,      setFiltPerfil]       = useState('');
  const [filtColaborador, setFiltColaborador]  = useState('');
  const [msgForm,         setMsgForm]          = useState({ user_id: '', telefone: '', nome: '', cpf: '', mensagem: '' });

 const fetchStatus = async () => {
  try {
    const r = await axios.get('/api/whatsapp/status', {
      params: { _t: Date.now() }
    });
    setStatus(r.data);
  } catch {}
};

  const fetchLogs = async () => {
    try { const r = await axios.get('/api/whatsapp/logs'); setLogs(r.data); } catch {}
  };
  const iniciarBot = async () => {
    try { await axios.post('/api/whatsapp/iniciar'); } catch {}
  };
  const desconectar = async () => {
    if (!window.confirm('Desconectar WhatsApp?')) return;
    try { await axios.post('/api/whatsapp/desconectar'); setStatus(null); } catch {}
  };

 useEffect(() => {
  fetchStatus();
  fetchLogs();

  // Status mais frequente (QR code precisa atualizar rápido)
  const ivStatus = setInterval(() => fetchStatus(), 8000);

  // Logs menos frequente (não precisa ser em tempo real)
  const ivLogs = setInterval(() => fetchLogs(), 30000);

  return () => {
    clearInterval(ivStatus);
    clearInterval(ivLogs);
  };
}, []);

  // Rede do coord selecionado (ou própria rede no modo coord)
  const redeDoCoord       = filtCoord ? getSubtree(users, filtCoord) : [];
  const perfisDisponiveis = [...new Set(redeDoCoord.map(u => u.role))].filter(Boolean);
  const colaboradoresCom  = redeDoCoord.filter(u => (!filtPerfil || u.role === filtPerfil) && u.telefone);

  const resetFiltros = () => {
    setFiltPerfil(''); setFiltColaborador('');
    setMsgForm({ user_id: '', telefone: '', nome: '', cpf: '', mensagem: '' });
  };

  const selecionarUsuario = (uid) => {
    const u = redeDoCoord.find(x => String(x.id) === String(uid));
    if (!u) { resetFiltros(); return; }
    setFiltColaborador(uid);
    setMsgForm({ user_id: u.id, telefone: u.telefone, nome: u.nome, cpf: u.cpf,
      mensagem: gerarMsgVerificacao(u.nome, u.cpf) });
  };

  const enviarUm = async () => {
    if (!status?.conectado) return alert('Bot não conectado.');
    if (!msgForm.telefone)  return alert('Selecione um colaborador.');
    setSending(true);
    try {
      await axios.post('/api/whatsapp/enviar', {
        user_id: msgForm.user_id, telefone: msgForm.telefone, mensagem: msgForm.mensagem,
      });
      alert(`✅ Mensagem enviada para ${msgForm.nome}!`);
      resetFiltros();
      fetchLogs();
    } catch (e) { alert('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSending(false); }
  };

  const enviarGrupo = async () => {
    if (!status?.conectado) return alert('Bot não conectado.');
    const lista = colaboradoresCom;
    if (!lista.length) return alert('Nenhum colaborador com telefone na seleção.');
    if (!window.confirm(`Enviar para ${lista.length} colaboradores?`)) return;
    setSending(true);
    let ok = 0, erro = 0;
    for (const u of lista) {
      try {
        await axios.post('/api/whatsapp/enviar', {
          user_id: u.id, telefone: u.telefone,
          mensagem: gerarMsgVerificacao(u.nome, u.cpf),
        });
        ok++;
        await new Promise(r => setTimeout(r, 1500));
      } catch { erro++; }
    }
    setSending(false);
    alert(`Enviado: ${ok} | Erro: ${erro}`);
    fetchLogs();
  };

  const modoAdmin = !coordId;

  return (
    <div>
      <h2 className="page-title">🤖 Bot WhatsApp — Verificação</h2>

      {/* Barra de status + abas internas */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20,
        background: '#fff', border: '2px solid var(--primary)', borderRadius: 12,
        padding: '12px 18px', flexWrap: 'wrap' }}>
        <StatusDot status={status} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            ['painel', '📱 Conexão'],
            ['enviar', '📤 Enviar'],
            ['logs',   '📋 Histórico'],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setBotTab(id)}
              style={{ background: botTab === id ? 'var(--primary)' : '#fff',
                color: botTab === id ? '#fff' : 'var(--primary)',
                border: '2px solid var(--primary)', borderRadius: 8,
                padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── PAINEL DE CONEXÃO ── */}
      {botTab === 'painel' && (
        <div className="charts-grid">
          <div className="card">
            <h3 className="card-title">📱 Conexão WhatsApp</h3>
            {status?.conectado ? (
              <div style={{ textAlign: 'center', padding: 30 }}>
                <div style={{ fontSize: 52 }}>✅</div>
                <p style={{ color: 'var(--success)', fontWeight: 700, fontSize: 16, marginTop: 12 }}>Conectado!</p>
                <p style={{ color: '#555', fontSize: 13 }}>+55 {status.numero}</p>
                <button onClick={desconectar} className="btn btn-outline btn-sm"
                  style={{ marginTop: 16, borderColor: 'var(--danger)', color: 'var(--danger)' }}>
                  🔌 Desconectar
                </button>
              </div>
            ) : status?.iniciando ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                {status.qrcode ? (
                  <>
                    <p style={{ color: 'var(--primary)', fontWeight: 700, marginBottom: 12 }}>📷 Escaneie com seu WhatsApp:</p>
                    <img src={status.qrcode} alt="QR Code" className="qr-img" style={{ width: 220, height: 220 }} />
                    <p style={{ fontSize: 12, color: '#555', marginTop: 10 }}>
                      WhatsApp → Menu → Dispositivos conectados → Conectar dispositivo
                    </p>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 44 }}>⏳</div>
                    <p style={{ color: 'var(--warning)', fontWeight: 700, marginTop: 12 }}>Gerando QR Code...</p>
                    <p style={{ fontSize: 12, color: '#555' }}>Aguarde 15–30 segundos</p>
                  </>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 30 }}>
                <div style={{ fontSize: 52 }}>📱</div>
                <p style={{ color: '#555', marginTop: 12, fontSize: 13 }}>
                  Conecte seu WhatsApp para enviar mensagens de verificação.
                </p>
                <button onClick={iniciarBot} className="btn btn-wa" style={{ marginTop: 16 }}>
                  🚀 Conectar WhatsApp
                </button>
              </div>
            )}
          </div>

          <div className="card">
            <h3 className="card-title">📊 Estatísticas</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Mensagens Enviadas',   val: logs.filter(l => l.tipo === 'ENVIADA').length,    color: 'var(--primary)' },
                { label: 'Respostas Recebidas',  val: logs.filter(l => l.tipo === 'RECEBIDA').length,   color: '#1a56db' },
                { label: 'Confirmaram ✅',        val: logs.filter(l => l.resposta === '1').length,      color: 'var(--success)' },
                { label: 'Não Confirmaram ❌',    val: logs.filter(l => l.resposta === '0').length,      color: 'var(--danger)' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', background: '#f0f8ff', borderRadius: 8, padding: '10px 16px',
                  border: `1px solid ${s.color}33` }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{s.label}</span>
                  <span style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── ENVIAR ── */}
      {botTab === 'enviar' && (
        <div className="card">
          <h3 className="card-title">📤 Enviar Mensagem de Verificação</h3>

          {!status?.conectado && (
            <div className="alert alert-danger">
              ⚠️ Bot não conectado. Vá em <strong>Conexão</strong> e inicie primeiro.
            </div>
          )}

          {/* Filtros hierárquicos */}
          <div style={{ background: '#f0f8ff', border: '1px solid var(--primary)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', letterSpacing: 1, marginBottom: 12 }}>
              🔎 FILTROS HIERÁRQUICOS
            </div>
            <div className="bot-filters" style={{ display: 'grid', gridTemplateColumns: modoAdmin ? '1fr 1fr 1fr' : '1fr 1fr', gap: 12 }}>

              {/* Filtro 1: Coord (só no modo ADMIN) */}
              {modoAdmin && (
                <div>
                  <label className="r-label">1. COORDENADOR GERAL</label>
                  <select className="r-select" value={filtCoord}
                    onChange={e => { setFiltCoord(e.target.value); resetFiltros(); }}>
                    <option value="">-- Selecione --</option>
                    {coordsGerais.map(c => (
                      <option key={c.id} value={c.id}>{c.nome || c.login}</option>
                    ))}
                  </select>
                  {filtCoord && (
                    <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 4, fontWeight: 600 }}>
                      Rede: {redeDoCoord.length} | Com tel: {redeDoCoord.filter(u => u.telefone).length}
                    </div>
                  )}
                </div>
              )}

              {/* Filtro 2: Perfil */}
              <div>
                <label className="r-label">{modoAdmin ? '2.' : '1.'} FILTRAR POR PERFIL</label>
                <select className="r-select" value={filtPerfil}
                  disabled={!filtCoord}
                  onChange={e => { setFiltPerfil(e.target.value); setFiltColaborador(''); setMsgForm({ user_id: '', telefone: '', nome: '', cpf: '', mensagem: '' }); }}>
                  <option value="">Todos os perfis</option>
                  {perfisDisponiveis.map(p => (
                    <option key={p} value={p}>
                      {roleLabel(p)} ({redeDoCoord.filter(u => u.role === p).length})
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 4, fontWeight: 600 }}>
                  {colaboradoresCom.length} com telefone
                </div>
              </div>

              {/* Filtro 3: Colaborador */}
              <div>
                <label className="r-label">{modoAdmin ? '3.' : '2.'} COLABORADOR ESPECÍFICO</label>
                <select className="r-select" value={filtColaborador}
                  disabled={!filtCoord}
                  onChange={e => selecionarUsuario(e.target.value)}>
                  <option value="">-- Envio em grupo --</option>
                  {colaboradoresCom.map(u => (
                    <option key={u.id} value={u.id}>{u.nome || u.login} — {u.telefone}</option>
                  ))}
                </select>
              </div>
            </div>

            {filtColaborador && msgForm.nome && (
              <div className="alert alert-success" style={{ marginTop: 10, marginBottom: 0 }}>
                📱 <strong>{msgForm.nome}</strong> — {msgForm.telefone} — CPF: {msgForm.cpf || 'não informado'}
              </div>
            )}
          </div>

          {/* Mensagem */}
          <div className="field-group">
            <label className="r-label">MENSAGEM</label>
            <textarea className="r-input" style={{ height: 120, resize: 'vertical' }}
              value={msgForm.mensagem}
              onChange={e => setMsgForm({ ...msgForm, mensagem: e.target.value })}
              placeholder="Selecione um colaborador para preencher automaticamente..." />
          </div>

          {/* Botões */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
            <button onClick={enviarUm}
              disabled={sending || !status?.conectado || !msgForm.telefone}
              className="btn btn-wa" style={{ flex: 1, opacity: (!msgForm.telefone || sending) ? 0.5 : 1 }}>
              {sending ? 'Enviando...' : '📲 Enviar para Colaborador Selecionado'}
            </button>
            <button onClick={enviarGrupo}
              disabled={sending || !status?.conectado || !filtCoord}
              className="btn" style={{ opacity: (!filtCoord || sending) ? 0.5 : 1 }}>
              {sending ? 'Enviando...' : `📢 Enviar para Todos (${filtColaborador ? 1 : colaboradoresCom.length})`}
            </button>
          </div>
        </div>
      )}

      {/* ── HISTÓRICO ── */}
      {botTab === 'logs' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ color: 'var(--primary)', fontWeight: 700, margin: 0 }}>Histórico ({logs.length})</h3>
            <button onClick={fetchLogs} className="btn btn-sm">🔄 Atualizar</button>
          </div>
          <div className="table-wrap">
            <table className="r-table">
              <thead>
                <tr>
                  {['DATA/HORA', 'NOME', 'CPF', 'TELEFONE', 'TIPO', 'RESPOSTA', 'VALIDAÇÃO WA'].map(h =>
                    <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {logs.length === 0
                  ? <tr><td colSpan={7} style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>Nenhuma mensagem registrada</td></tr>
                  : logs.map(l => (
                    <tr key={l.id}>
                      <td>{new Date(l.created_at).toLocaleString('pt-BR')}</td>
                      <td>{l.nome || '-'}</td>
                      <td>{l.cpf  || '-'}</td>
                      <td>{l.telefone}</td>
                      <td>
                        <span className="pill" style={{ background: l.tipo === 'ENVIADA' ? 'var(--primary)' : '#25D36622', color: l.tipo === 'ENVIADA' ? '#fff' : 'var(--success)' }}>
                          {l.tipo === 'ENVIADA' ? '📤 Enviada' : '📩 Recebida'}
                        </span>
                      </td>
                      <td>
                        {l.resposta === '1' && <span className="pill" style={{ background: '#d1fae5', color: 'var(--success)' }}>1 ✅ Sim</span>}
                        {l.resposta === '0' && <span className="pill" style={{ background: '#fee2e2', color: 'var(--danger)'  }}>0 ❌ Não</span>}
                        {!l.resposta && <span style={{ color: '#aaa', fontSize: 11 }}>—</span>}
                      </td>
                      <td>
                        <span className="pill" style={{ background: waColor(l.validacao_whatsapp) + '22', color: waColor(l.validacao_whatsapp) }}>
                          {waLabel(l.validacao_whatsapp || 'INATIVO')}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Helpers internos
const WA_LABEL = { INATIVO: '⬜ Inativo', ATIVO: '✅ Validado', NAO_VALIDADO: '❌ Não Validado' };
const WA_COLOR = { INATIVO: '#6b7280', ATIVO: '#057a55', NAO_VALIDADO: '#cc0000' };
const waLabel  = (v) => WA_LABEL[v] || '⬜ Inativo';
const waColor  = (v) => WA_COLOR[v] || '#6b7280';

function StatusDot({ status }) {
  const cor  = status?.conectado ? '#25D366' : status?.iniciando ? '#f59e0b' : '#cc0000';
  const txt  = status?.conectado
    ? `✅ Conectado — +55 ${status.numero}`
    : status?.iniciando ? '⏳ Aguardando QR Code...' : '❌ Desconectado';
  return (
    <>
      <div style={{ width: 13, height: 13, borderRadius: '50%', background: cor, boxShadow: `0 0 8px ${cor}` }} />
      <span style={{ fontWeight: 700, color: cor, fontSize: 13 }}>{txt}</span>
    </>
  );
}
